import type { IPAMetricKey } from '~/modules/analytics/types';
import type { DataSeries } from '@shopify/polaris-viz';

import { CardAnalyticEmpty, numberWithCommas, PA_METRIC_TOOLTIP } from '~/modules/analytics';
import { BlockStack, Card, Icon, InlineStack, Text } from '@shopify/polaris';
import { PADonutChartSkeleton } from './common/PADonutChartSkeleton';
import { ChevronRightIcon } from '@shopify/polaris-icons';
import { useWindowSize } from '~/presentation/common';
import { DonutChart } from '@shopify/polaris-viz';
import { GTooltipCard } from '~/presentation/ui';
import { useToggle } from '~/hooks/useToggle';
import { PAChartProvider } from '../contexts';
import { cls } from '~/utils';

/**
 * Interface for donut chart data
 */
interface PVDonutChartData extends DataSeries {
  metadata: {
    trend: {
      value?: string;
      direction: 'upward' | 'downward';
      trend: 'positive' | 'negative';
    };
  };
}

/**
 * Props for the PACardDonutChart component
 */
interface PACardDonutChartProps {
  label: string;
  tooltipKey: IPAMetricKey;
  data: PVDonutChartData[];
  isLoading?: boolean;
  isEmptyMetricData?: boolean;
  onClick: () => void;
}

/**
 * Component to render a donut chart card with session data
 */
export const PACardDonutChart: React.FC<PACardDonutChartProps> = ({
  label,
  tooltipKey,
  data,
  isLoading,
  isEmptyMetricData,
  onClick,
}) => {
  const tooltip = PA_METRIC_TOOLTIP[tooltipKey];
  const { windowWidth, windowSize } = useWindowSize();

  const { setFalse: setFalseHover, setTrue: setTrueHover, value: isHover } = useToggle(false);

  if (isLoading) {
    return <PADonutChartSkeleton />;
  }

  const formatValue = (value: string | number | null) => {
    return numberWithCommas(`${value}`);
  };

  return (
    <div onMouseEnter={setTrueHover} onMouseLeave={setFalseHover}>
      <Card>
        <BlockStack gap="200">
          <InlineStack>
            <div
              className="hover:cursor-pointer hover:text-[--p-color-text-link-hover]"
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              <InlineStack>
                <GTooltipCard tooltip={tooltip}>
                  <Text as="h3" variant="headingMd">
                    {label}
                  </Text>
                </GTooltipCard>

                {isHover && <Icon source={ChevronRightIcon} tone="inherit" />}
              </InlineStack>
            </div>
          </InlineStack>
          <div
            className={cls('flex items-center justify-center', {
              'max-h-[250px] overflow-hidden': windowWidth.lg && windowSize.width < 1500,
            })}
          >
            {isEmptyMetricData ? (
              <CardAnalyticEmpty />
            ) : (
              <PAChartProvider minHeight={294}>
                <DonutChart
                  data={data}
                  legendPosition="left"
                  showLegendValues
                  showLegend
                  theme="Light"
                  tooltipOptions={{
                    valueFormatter: formatValue,
                  }}
                  labelFormatter={formatValue}
                />
              </PAChartProvider>
            )}
          </div>
        </BlockStack>
      </Card>
    </div>
  );
};
